import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _excluded = ["children", "primitive", "viewport", "size", "color", "bold", "typescale", "theme", "tabIndex", "fontSize", "lineheight", "fontWeight", "config", "superscriptSmall"];

var _templateObject;

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { colors } from '@vds-core/theme';
import TypographyConfig from '../../typographyConfig';
import fontColorPropType from '../../util/fontColorPropType';
import { Typography } from '../Typography';
import { withVDSManager } from '@vds-core/utilities';
var propTypes = {
  /**
   * Primitive used for the component.
   */
  primitive: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'span', 'p']),

  /**
   * Viewport the font styling is based on. Desktop and tablet are the same size.
   */
  viewport: PropTypes.oneOf(['desktop', 'tablet', 'mobile']),

  /**
   * Specifies the font size of the Feature.
   */
  size: PropTypes.oneOf(['XSmall', 'small', 'medium', 'large', 'XLarge']),

  /**
   * Text to be displayed.
   */
  children: PropTypes.node,

  /**
   * Color of text to be displayed.
   */
  color: fontColorPropType,

  /**
   * @ignore
   * If selected, Feature will be rendered in bold styling.
   */
  bold: PropTypes.bool,

  /**
   * @ignore
   * Specifies the format for the Feature.
   */
  typescale: PropTypes.oneOf(['VDS', 'MVP']),

  /**
   * The tab index of the component.
   */
  tabIndex: PropTypes.number,

  /**
   * @ignore
   * The size of the text to be displayed.
   */
  fontSize: PropTypes.number,

  /**
   * @ignore
   * The height of the line.
   */
  lineHeight: PropTypes.number,

  /**
   * @ignore
   * The weight of the font.
   */
  fontWeight: PropTypes.string,

  /**
   * @ignore
   * config object for typography
   */
  config: PropTypes.object,

  /**
   * If passed, custom superscript styles will be applied to any <Super> child elements
   */
  superscriptSmall: PropTypes.bool
};
var defaultProps = {
  viewport: 'desktop',
  size: 'medium',
  children: null,
  color: colors.black,
  bold: true,
  tabIndex: undefined,
  superscriptSmall: false
};
var primitiveMap = {
  extraSmall: 'span',
  small: 'h5',
  medium: 'h4',
  large: 'h3',
  extraLarge: 'h2',
  extraExtraLarge: 'h1'
};
var StyledFeature = /*#__PURE__*/styled(Typography).withConfig({
  displayName: "StyledFeature",
  componentId: "sc-31qxhl-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  &:active,\n  &:hover {\n    outline: none;\n  }\n"])));

var _getFontWeight = function _getFontWeight(_ref) {
  var fontWeight = _ref.fontWeight,
      bold = _ref.bold;
  return fontWeight ? fontWeight : bold ? '700' : '400';
};

var Feature = function Feature(props) {
  var children = props.children,
      primitive = props.primitive,
      viewport = props.viewport,
      size = props.size,
      color = props.color,
      bold = props.bold,
      typescale = props.typescale,
      theme = props.theme,
      tabIndex = props.tabIndex,
      fontSizeProp = props.fontSize,
      lineHeightProp = props.lineheight,
      fontWeightProp = props.fontWeight,
      config = props.config,
      superscriptSmall = props.superscriptSmall,
      rest = _objectWithoutProperties(props, _excluded); // if viewport has mobile on it, then it's mobile, otherwise is desktop.
  // we avoid here mapping mobileSmall, to mobile, tablet and desktopSmall to desktop.


  var _viewport = /mobile/.test(viewport) ? 'mobile' : 'desktop';

  var sizeToUse = size === '2XLarge' ? 'XLarge' : size;
  var fontConfig = config && config['VDS'][_viewport][sizeToUse];

  var fontWeight = _getFontWeight({
    fontWeight: fontWeightProp,
    bold: bold
  });

  var fontFamily = fontConfig && fontConfig.fontFamily;

  function mapChildren() {
    if (!children) return null;
    return React.Children.map(children, function (child, index) {
      if (child && /*#__PURE__*/React.isValidElement(child) && child.type && child.type.displayName && child.type.displayName === 'Super') {
        return /*#__PURE__*/React.cloneElement(child, {
          fontConfig: fontConfig,
          superscriptSmall: superscriptSmall
        });
      } else {
        return child;
      }
    });
  }

  return /*#__PURE__*/React.createElement(Fragment, null, config && /*#__PURE__*/React.createElement(StyledFeature, _extends({}, rest, {
    size: sizeToUse,
    fontSize: fontSizeProp ? fontSizeProp : fontConfig.fontSize,
    fontWeight: fontWeight,
    fontFamily: fontFamily,
    lineheight: lineHeightProp ? lineHeightProp : fontConfig.lineHeight,
    primitive: primitive ? primitive : primitiveMap[sizeToUse],
    color: color,
    tabIndex: tabIndex,
    "aria-hidden": tabIndex < 0
  }), mapChildren()), !config && null);
};

Feature.propTypes = propTypes;
Feature.defaultProps = defaultProps;
export default withVDSManager(Feature);